/**
 * features/invoices/invoicesWrite.ts
 * ------------------------------------------------------------------
 * Writes for invoices. Drafts carry no number and are freely editable.
 * Issuing freezes the client + VAT snapshots, reserves a gapless
 * number, and locks the document (void-or-credit thereafter — never
 * hard-deleted, numbers never reused).
 * ------------------------------------------------------------------
 */
import { collection, deleteDoc, doc } from 'firebase/firestore';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { nextNumber } from '@/lib/numbering';
import {
  COLLECTIONS,
  type Client,
  type ClientSnapshot,
  type DocumentTotals,
  type LineItem,
  type VatSnapshot,
} from '@/types';

/** Freeze the client's details for the document. */
export function clientSnapshot(c: Client): ClientSnapshot {
  const snap: ClientSnapshot = { clientId: c.id, name: c.name };
  if (c.tin?.trim()) snap.tin = c.tin.trim();
  if (c.vatNumber?.trim()) snap.vatNumber = c.vatNumber.trim();
  if (c.email?.trim()) snap.email = c.email.trim();
  if (c.billingAddress?.trim()) snap.billingAddress = c.billingAddress.trim();
  return snap;
}

export interface InvoicePayload {
  clientId: string;
  client: ClientSnapshot;
  issueDate: string; // yyyy-mm-dd
  dueDate: string; // yyyy-mm-dd
  lineItems: LineItem[];
  totals: DocumentTotals;
  vat: VatSnapshot;
  notes?: string;
}

function baseDoc(p: InvoicePayload) {
  return {
    clientId: p.clientId,
    client: p.client,
    issueDate: p.issueDate,
    dueDate: p.dueDate,
    lineItems: p.lineItems,
    totals: p.totals,
    currency: 'BWP' as const,
    vat: p.vat,
    notes: p.notes,
    updatedAt: new Date().toISOString(),
  };
}

/** Create or update a DRAFT (no number, status 'draft'). */
export async function saveDraft(
  p: InvoicePayload,
  actor: string,
  id?: string,
): Promise<string> {
  const data = {
    ...baseDoc(p),
    number: null,
    status: 'draft' as const,
    amountPaid: 0,
    balance: p.totals.total,
  };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.invoices, id), data);
    return id;
  }
  const ref = await addDoc(collection(db, COLLECTIONS.invoices), {
    ...data,
    createdAt: new Date().toISOString(),
    createdBy: actor,
  });
  return ref.id;
}

/**
 * Issue: reserve a gapless number for the issue-date's year, lock the
 * frozen snapshots, set status 'issued', and seed the balance.
 * Works whether the invoice was a saved draft (id) or issued directly.
 */
export async function issueInvoice(
  p: InvoicePayload,
  actor: string,
  id?: string,
): Promise<{ id: string; number: string }> {
  const number = await nextNumber('invoice', new Date(p.issueDate));
  const now = new Date().toISOString();
  const data = {
    ...baseDoc(p),
    number,
    status: 'issued' as const,
    amountPaid: 0,
    balance: p.totals.total,
    issuedAt: now,
  };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.invoices, id), data);
    return { id, number };
  }
  const ref = await addDoc(collection(db, COLLECTIONS.invoices), {
    ...data,
    createdAt: now,
    createdBy: actor,
  });
  return { id: ref.id, number };
}

/** Void an issued invoice (number retained, never reused). */
export async function voidInvoice(id: string): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.invoices, id), {
    status: 'void',
    updatedAt: new Date().toISOString(),
  });
}

/** Discard a draft outright (drafts never held a number, so the issued
 *  record stays gapless). Issued invoices must be voided, not deleted. */
export async function deleteDraft(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.invoices, id));
}
